import { UserIdentification } from "@sp-crm/core";
import posthog from "posthog-js";
import {
    enrichExceptionEvent,
    recordExceptionStep,
    setExceptionSink,
} from "./exception-sink";
import { crmHistory } from "./history";
import { buildVersion } from "./version";

let isConfigured = false;
let pendingCallbacks: Array<() => void> = [
    () => {
        posthog.reset();
    },
];

const posthogApiKey = "phc_d7GRBC0geaW6GXnp3Yzincyb8gEf6ibDeC5hESoeWBF";

type PosthogInitOptions = NonNullable<Parameters<typeof posthog.init>[1]>;

const basePosthogOptions = (): PosthogInitOptions => ({
    api_host: "https://app.posthog.com",
    autocapture: false,
    capture_pageleave: false,
    capture_pageview: false,
    capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: false,
        capture_console_errors: false,
    },
    tracing_headers: [window.location.hostname],
    before_send: enrichExceptionEvent,
});

const flushPendingCallbacks = () => {
    isConfigured = true;
    pendingCallbacks.forEach(callback => callback());
    pendingCallbacks = [];
};

const registerExceptionSink = () => {
    setExceptionSink({
        captureException: (error, properties) =>
            run(() => {
                posthog.captureException(error, properties);
            }),
        addExceptionStep: (message, properties) =>
            run(() => {
                posthog.addExceptionStep(message, properties);
            }),
    });
    run(() => {
        posthog.register({ build_version: buildVersion });
    });
};

let isInitialized = false;

const ensurePosthogInitialized = () => {
    if (isInitialized) {
        return;
    }
    isInitialized = true;
    posthog.init(posthogApiKey, {
        ...basePosthogOptions(),
        loaded: flushPendingCallbacks,
    });
    registerExceptionSink();
};

export const configureUserEvents = async () => {
    ensurePosthogInitialized();
    run(startDarkModeTracking);
    crmHistory.listen(location => {
        posthog.capture("$pageview");
        recordExceptionStep("navigate", { path: location.pathname });
    });
    posthog.capture("$pageview");
};

export const configureExceptionCapture = (environment: string) => {
    ensurePosthogInitialized();
    run(() => {
        posthog.register({ environment });
    });
};

const run = (cb: () => void) => {
    if (isConfigured) {
        cb();
    } else {
        pendingCallbacks.push(cb);
    }
};

interface HasBeacon {
    Beacon?: (action: string, config?: unknown) => void;
}

let beaconInitialized = false;
let pendingBeaconIdentification: { email: string; name: string } | null = null;

export const onBeaconInitialized = (initialized: boolean) => {
    beaconInitialized = initialized;
    if (initialized && pendingBeaconIdentification) {
        const Beacon = (window as HasBeacon).Beacon;
        if (Beacon) {
            Beacon("identify", pendingBeaconIdentification);
        }
        pendingBeaconIdentification = null;
    }
};

export const identifyUser = (userIdentification: UserIdentification) => {
    const Beacon = (window as HasBeacon).Beacon;
    if (Beacon) {
        if (beaconInitialized) {
            Beacon("identify", {
                email: userIdentification.email,
                name: userIdentification.name,
            });
        } else {
            pendingBeaconIdentification = {
                email: userIdentification.email,
                name: userIdentification.name,
            };
        }
    }
    run(() => {
        posthog.identify(userIdentification.userId, {
            tenantId: userIdentification.tenantId,
            billableEntityId: userIdentification.billableEntityId,
            sku: userIdentification.sku,
        });
    });
};

export const logoutEvent = () => {
    run(() => {
        posthog.reset();
    });
    const Beacon = (window as HasBeacon).Beacon;
    if (Beacon && beaconInitialized) {
        Beacon("logout");
    }
    pendingBeaconIdentification = null;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const userEvent = [
    "2fa-disable-fail",
    "2fa-disable-start",
    "2fa-disable-success",
    "2fa-enable-fail",
    "2fa-enable-resent",
    "2fa-enable-start",
    "2fa-enable-success",
    "2fa-password-reset-start",
    "2fa-password-reset-fail",
    "2fa-password-reset-success",
    "2fa-password-reset-resend",
    "2fa-fail",
    "2fa-resend",
    "2fa-start",
    "2fa-success",
    "ai-summary-generate",
    "client-action",
    "client-status-change",
    "community-missing-recipients",
    "home-care-client-status-change",
    "community-search-advanced-filter",
    "create-client",
    "create-home-care-client",
    "initiate-voice-call",
    "marketing-plan-send-email",
    "marketing-plan-add-phone",
    "marketing-plan-add-email",
    "marketing-plan-mark-done",
    "marketing-plan-visit-mark-done",
    "marketing-plan-visit-canceled",
    "marketing-plan-visit-rescheduled",
    "marketing-plan-snooze",
    "marketing-plan-unsnooze",
    "marketing-plan-customize",
    "marketing-plan-revert",
    "marketing-plan-touchpoint-create",
    "marketing-plan-touchpoint-delete",
    "marketing-plan-touchpoint-update",
    "marketing-touchpoint-create",
    "marketing-touchpoint-delete",
    "marketing-touchpoint-update",
    "message-send",
    "open-feature-announcements",
    "premium-callout",
    "referral-pipeline-add-card",
    "referral-pipeline-auto-add-toggle",
    "referral-pipeline-board-view",
    "referral-pipeline-create",
    "referral-pipeline-delete",
    "referral-pipeline-move-card",
    "referral-pipeline-remove-card",
    "referral-pipeline-rename",
    "referral-pipeline-stage-create",
    "referral-pipeline-stage-delete",
    "referral-pipeline-stage-rename",
    "referral-pipeline-stage-reorder",
    "referral-tier-archive",
    "referral-tier-assign",
    "referral-tier-create",
    "referral-tier-filter",
    "referral-tier-office-customize",
    "referral-tier-office-revert",
    "referral-tier-update",
    "request-community-availability",
    "request-community-availability-success",
    "request-community-availability-update",
    "request-community-availability-update-success",
    "sms-send",
    "sms-setup",
    "sms-thread-view",
    "task-complete",
    "task-create",
    "task-note-saved",
    "upload-client-form",
    "upload-client-form-start",
    "upload-client-form-upload",
    "user-dark-mode-preference",
    "user-layout-rendered",
    "user-login",
    "user-preview-design-toggle",
    "view-adhoc-report-results",
    "view-saved-report-results",
    "visit-add-click",
    "visit-save",
    "visit-outcome-recorded",
    "print-client-form",
    "print-community-form",
    "community-contact-signup-loaded",
    "community-contact-signup-submit",
    "community-contact-data-request-loaded",
    "community-contact-data-request-submit",
    "request-community-pricing",
    "request-community-pricing-success",
    "request-community-pricing-signup",
    "request-community-pricing-signup-success",
    "mailbox-sync-initiate",
    "mailbox-sync-repair",
    "message-reply",
    "message-reply-all",
    "email-send-another-prompt",
    "preference-change",
    "time-tracking-enabled",
    "time-tracking-disabled",
    "timer-started",
    "timer-stopped",
    "timer-paused",
    "timer-resumed",
    "time-entry-edited",
    "scan-business-card-start",
    "scan-business-card-photo-selected",
    "scan-business-card-submit",
    "scan-business-card-feedback-intent",
    "scan-business-card-feedback-submit",
    "contract-payment-schedule-save",
    "contract-payment-schedule-remove",
    "invoice-contract-prefill",
    "lead-source-create",
    "lead-source-update",
    "lead-source-delete",
    "workflow-builder-list-view",
    "workflow-builder-list-filter",
    "workflow-builder-create",
    "workflow-builder-step-view",
    "workflow-builder-blueprint-change",
    "workflow-builder-edit",
    "workflow-builder-setting-change",
    "workflow-builder-publish",
    "workflow-builder-unpublish",
    "workflow-builder-delete",
    "workflow-builder-never-runs-warning",
    "workflow-builder-detail-view",
    "workflow-builder-run-cancel",
] as const;
type UserEvent = (typeof userEvent)[number];

export const handleEvent = (
    name: UserEvent,
    properties: Record<string, unknown> = {},
) => {
    run(() => {
        const eventProperties = {
            ...properties,
        };
        posthog.capture(name, eventProperties);
        posthog.addExceptionStep(name, eventProperties);
    });
};

const trackDarkModePreference = () => {
    if (typeof window === "undefined" || !window.matchMedia) {
        handleEvent("user-dark-mode-preference", {
            prefersDarkMode: false,
            colorScheme: "unknown",
        });
        return;
    }

    const darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const isDarkMode = darkModeQuery.matches;

    handleEvent("user-dark-mode-preference", {
        prefersDarkMode: isDarkMode,
        colorScheme: isDarkMode ? "dark" : "light",
    });
};

const startDarkModeTracking = () => {
    trackDarkModePreference();

    if (typeof window === "undefined" || !window.matchMedia) {
        return;
    }

    const darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event: MediaQueryListEvent) => {
        handleEvent("user-dark-mode-preference", {
            prefersDarkMode: event.matches,
            colorScheme: event.matches ? "dark" : "light",
        });
    };

    darkModeQuery.addEventListener("change", handleChange);
};
